import { Component, OnInit, ViewChild, ElementRef, Input, Optional, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialogRef } from '@angular/material/dialog';

import { ConfigurationsService } from 'src/app/core/services/configurations.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from 'src/app/core/services/utils.service';

import { MenuItem } from 'src/app/core/models/menuItem';

import * as _ from 'lodash';

@Component({
  selector: 'reorder-menu',
  templateUrl: './reorder-menu.component.html',
  styleUrls: ['./reorder-menu.component.scss']
})
export class ReorderMenuComponent implements OnInit {
  @ViewChild('listContainer') listContainerRef!: ElementRef;
  @Input() layoutType: 'modal' | 'sidebar' = 'modal';
  @Output() menuChanged$: EventEmitter<MenuItem[]> = new EventEmitter<MenuItem[]>();

  userMenuItems: MenuItem[] = [];
  selectedIndex: number | null = null;
  mainColor: string;

  listModified: boolean = false;

  constructor(private _configurationsService: ConfigurationsService,
    @Optional() private _matDialogRef: MatDialogRef<ReorderMenuComponent>,
    private _translateService: TranslateService,
    private _utilsService: UtilsService) { }

  ngOnInit(): void {
    this.mainColor = this._utilsService.getMainColor();
    this._configurationsService.getUserMenuConfigFromAPI().subscribe((menu) => {
      this.userMenuItems = _.cloneDeep(menu);
      if (this.layoutType === 'sidebar') {
        this.menuChanged$.emit(this.setMenu());
      }
    });
  }

  onSave() {
    const orderedMenu = this.setMenu();
    this._configurationsService.userInfo.menu = orderedMenu;
    this._configurationsService.onChangeMenuOrder.next(orderedMenu);
    this._configurationsService.saveMenuConfiguration(orderedMenu).subscribe();
    this._matDialogRef.close();
  }

  select(index: number) {
    this.selectedIndex = this.selectedIndex === index ? null : index;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.userMenuItems, event.previousIndex, event.currentIndex);
    this.selectedIndex = event.currentIndex;
    this.listModified = true;

    this.layoutType === 'sidebar' && this.menuChanged$.emit(this.setMenu());
  }

  moveUp() {
    [this.userMenuItems[this.selectedIndex! - 1], this.userMenuItems[this.selectedIndex!]] =
      [this.userMenuItems[this.selectedIndex!], this.userMenuItems[this.selectedIndex! - 1]];
    this.selectedIndex!--;
    this.listModified = true;

    this.layoutType === 'sidebar' && this.menuChanged$.emit(this.setMenu());
  }

  moveDown() {
    [this.userMenuItems[this.selectedIndex! + 1], this.userMenuItems[this.selectedIndex!]] =
      [this.userMenuItems[this.selectedIndex!], this.userMenuItems[this.selectedIndex! + 1]];
    this.selectedIndex!++;
    this.listModified = true;

    this.layoutType === 'sidebar' && this.menuChanged$.emit(this.setMenu());
  }

  moveSelectedToTop() {
    const [item] = this.userMenuItems.splice(this.selectedIndex!, 1);
    this.userMenuItems.unshift(item);
    this.selectedIndex = 0;
    this.listModified = true;
    setTimeout(() => {
      if (this.listContainerRef && this.listContainerRef.nativeElement) {
        this.listContainerRef.nativeElement.scrollTop = 0;
      }
    });

    this.layoutType === 'sidebar' && this.menuChanged$.emit(this.setMenu());
  }

  moveSelectedToBottom() {
    const [item] = this.userMenuItems.splice(this.selectedIndex!, 1);
    this.userMenuItems.push(item);
    this.selectedIndex = this.userMenuItems.length - 1;
    this.listModified = true;
    setTimeout(() => {
      if (this.listContainerRef && this.listContainerRef.nativeElement) {
        this.listContainerRef.nativeElement.scrollTop = this.listContainerRef.nativeElement.scrollHeight;
      }
    });

    this.layoutType === 'sidebar' && this.menuChanged$.emit(this.setMenu());
  }

  toggleMenuItem(index: number) {
    this.userMenuItems[index].hidden = !this.userMenuItems[index].hidden;
    this.listModified = true;

    this.layoutType === 'sidebar' && this.menuChanged$.emit(this.setMenu());
  }

  setMenu() {
    const menuItems = [...this.userMenuItems];
    menuItems.forEach((item, i) => {
      item.customOrder = null;
      item.customOrder = i + 1;
    });

    return menuItems;
  }

  getLabel(key: string): string {
    const label: string = this._translateService.instant('global.menu.' + key)
    const formattedKey = key
      .split(/[\s\-_]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    return label.includes('global.menu.') ? formattedKey : label;
  }

  onCancel() {
    this._matDialogRef.close();
  }
}
